/**
 * The client errors PostHog Error Tracking must NOT see.
 *
 * Error tracking is only useful while the issue list is signal. Six classes of
 * noise would otherwise drown it, and none of them is a bug in our code:
 *
 * 1. `AbortError` - every cancelled fetch (React Query cleanup, navigating away
 *    mid-request, an aborted upload) raises one. It means "we stopped on
 *    purpose".
 * 2. `ResizeObserver loop …` - a benign browser warning surfaced as a window
 *    error. It has no stack worth reading and fires in bursts on resize.
 * 3. `ChunkLoadError` - the tab is running a bundle that was replaced by a
 *    deploy. The FIRST one per tab session is REPORTED (once - we want to see
 *    that it happened) and triggers one reload onto the new build. Every
 *    further one in that session is ignored, so a genuinely broken deploy
 *    cannot turn one tab into an unbounded report loop.
 * 4. Network failures against the analytics endpoints themselves - reporting a
 *    failed PostHog request through PostHog is a feedback loop.
 * 5. Errors thrown from a browser extension's own frames. A password manager or
 *    an ad blocker injecting a broken script is not our crash, and we cannot
 *    fix it.
 * 6. Network failures raised while `navigator.onLine` is false. The user is on
 *    a train, not looking at a broken app.
 *
 * The same predicate backs both entry points, so autocaptured exceptions
 * (`before_send` in ./client) and hand-reported ones (`reportClientError` in
 * ./error-reporting) filter identically.
 */

import type { CaptureResult } from 'posthog-js';

/**
 * Substrings that identify a request to an analytics collector. `/_x/` is our
 * own same-origin PostHog ingestion proxy (see the rewrites in next.config.ts);
 * the rest cover direct hits when a rewrite is bypassed.
 */
const ANALYTICS_ENDPOINT_MARKERS = [
	'/_x/',
	'i.posthog.com',
	'posthog.com',
	'/ingest/',
	'googletagmanager.com',
	'google-analytics.com',
	'/gtm.js',
];

/** Messages a browser uses for a network-layer failure (no HTTP response). */
const NETWORK_FAILURE_MARKERS = [
	'failed to fetch',
	'networkerror',
	'network request failed',
	'load failed',
	'err_',
	'xhr',
	'fetch',
];

/** Protocols only a browser extension's own scripts are served over. */
const EXTENSION_STACK_MARKERS = [
	'chrome-extension://',
	'moz-extension://',
	'safari-web-extension://',
	'safari-extension://',
];

const CHUNK_LOAD_RETRY_STORAGE_KEY = 'naumu:chunk-load-error-retried';

/**
 * Delay before the post-deploy reload, so the one report we DO send for the
 * first ChunkLoadError has a chance to leave the page first.
 */
const CHUNK_LOAD_RELOAD_DELAY_MS = 500;

/**
 * Marks an `$exception` that `reportClientError` already ran this filter over.
 *
 * `captureException` goes through `capture()`, which runs `before_send` - so
 * without this a hand-reported error would be judged twice, and the ONE-SHOT
 * rules below (report the first ChunkLoadError, ignore the rest) would reject
 * on the second pass the very event they just admitted. Stripped inside
 * `dropIgnoredExceptionEvents`, so it never reaches PostHog.
 */
export const PRE_FILTERED_EXCEPTION_PROPERTY = '$naumu_prefiltered';

/** Mirrors the sessionStorage flag so the guard still works in private mode. */
let chunkLoadRetriedInMemory = false;

/** One reload per document, however many chunk errors arrive. */
let chunkLoadReloadScheduled = false;

interface ErrorShape {
	name: string;
	message: string;
	/** Joined frame locations, matched only against extension protocols. */
	stack: string;
}

/**
 * Flatten anything throwable into the two strings the predicates match on.
 *
 * Only ever used for MATCHING - neither value leaves this module, and
 * `reportClientError` never copies a message into event properties.
 */
const toErrorShape = (err: unknown): ErrorShape => {
	if (err instanceof Error) {
		return {
			name: err.name ?? '',
			message: err.message ?? '',
			stack: typeof err.stack === 'string' ? err.stack : '',
		};
	}
	if (typeof err === 'string') return { name: '', message: err, stack: '' };
	if (err && typeof err === 'object') {
		const candidate = err as {
			name?: unknown;
			message?: unknown;
			stack?: unknown;
		};
		return {
			name: typeof candidate.name === 'string' ? candidate.name : '',
			message: typeof candidate.message === 'string' ? candidate.message : '',
			stack: typeof candidate.stack === 'string' ? candidate.stack : '',
		};
	}
	return { name: '', message: '', stack: '' };
};

const isAbortError = ({ name, message }: ErrorShape): boolean => {
	if (name === 'AbortError') return true;
	// DOMException ABORT_ERR raised without a usable `name` (older Safari).
	const lower = message.toLowerCase();
	return (
		lower.includes('the user aborted a request') ||
		lower.includes('the operation was aborted') ||
		lower.includes('signal is aborted') ||
		lower.includes('aborted a request')
	);
};

const isResizeObserverLoop = ({ message }: ErrorShape): boolean =>
	message.toLowerCase().includes('resizeobserver loop');

const isChunkLoadError = ({ name, message }: ErrorShape): boolean => {
	if (name === 'ChunkLoadError') return true;
	const lower = message.toLowerCase();
	return (
		lower.includes('loading chunk') ||
		lower.includes('loading css chunk') ||
		lower.includes('chunkloaderror')
	);
};

const isNetworkFailure = ({ message }: ErrorShape): boolean => {
	const lower = message.toLowerCase();
	return NETWORK_FAILURE_MARKERS.some((marker) => lower.includes(marker));
};

const isAnalyticsNetworkError = (shape: ErrorShape): boolean => {
	const lower = shape.message.toLowerCase();
	if (!ANALYTICS_ENDPOINT_MARKERS.some((marker) => lower.includes(marker))) {
		return false;
	}
	return isNetworkFailure(shape);
};

/**
 * True when the throw came from a browser extension's own script.
 *
 * Password managers, ad blockers, translation add-ons and screen recorders all
 * inject scripts into the page, and anything they throw lands in our error
 * handlers as if it were ours. Their frames are served over an extension
 * protocol, which page code can never be, so the stack is unambiguous - and
 * there is nothing to fix on our side anyway.
 */
const isBrowserExtensionError = ({ stack }: ErrorShape): boolean => {
	if (!stack) return false;
	const lower = stack.toLowerCase();
	return EXTENSION_STACK_MARKERS.some((marker) => lower.includes(marker));
};

/**
 * True when a network-layer failure happened while the browser reported itself
 * offline. `navigator.onLine === false` is only conclusive in the negative
 * direction (true does not prove reachability), which is exactly the direction
 * used here: a fetch that failed with no connection is the network, not a bug.
 */
const isOfflineNetworkError = (shape: ErrorShape): boolean => {
	if (typeof navigator === 'undefined') return false;
	if (navigator.onLine !== false) return false;
	return isNetworkFailure(shape);
};

const readChunkLoadRetryFlag = (): boolean => {
	if (typeof window === 'undefined') return false;
	try {
		return window.sessionStorage.getItem(CHUNK_LOAD_RETRY_STORAGE_KEY) === '1';
	} catch {
		return false;
	}
};

const hasChunkLoadRetryHappened = (): boolean =>
	chunkLoadRetriedInMemory || readChunkLoadRetryFlag();

/**
 * Persist the retry flag, and report whether it will actually survive a reload.
 *
 * The answer matters: if sessionStorage is unavailable (private mode, storage
 * blocked) the flag cannot outlive the document, so the next document would
 * decide it is the first one all over again. Reloading on a flag that does not
 * persist is an infinite reload loop, so the caller declines to reload at all
 * and settles for reporting. The in-memory mirror is still set, which keeps
 * this document's own reports bounded.
 */
const markChunkLoadRetry = (): boolean => {
	chunkLoadRetriedInMemory = true;
	if (typeof window === 'undefined') return false;
	try {
		window.sessionStorage.setItem(CHUNK_LOAD_RETRY_STORAGE_KEY, '1');
		return window.sessionStorage.getItem(CHUNK_LOAD_RETRY_STORAGE_KEY) === '1';
	} catch {
		return false;
	}
};

/**
 * Reload the tab onto the current build, at most once per document.
 *
 * This is the half that was missing: arming the retry flag without reloading
 * left the user staring at a broken page, because a `ChunkLoadError` means the
 * bundle this document was built against no longer exists on the server, and no
 * amount of retrying in-page can recover it.
 *
 * Deferred so the exception we just decided to report can leave first, and
 * fully guarded - a reload that throws must not become the error.
 */
const scheduleChunkLoadReload = (): void => {
	if (typeof window === 'undefined') return;
	if (chunkLoadReloadScheduled) return;
	chunkLoadReloadScheduled = true;
	window.setTimeout(() => {
		try {
			window.location.reload();
		} catch {
			// Sandboxed frame or a stubbed location; nothing further to try.
		}
	}, CHUNK_LOAD_RELOAD_DELAY_MS);
};

/**
 * True when this error is expected noise and must not reach PostHog.
 *
 * NOTE: calling this for a `ChunkLoadError` has a side effect by design - the
 * first one arms the retry flag, schedules the reload, and IS reported; every
 * later one in the session is ignored. Call it EXACTLY once per error, which is
 * what `PRE_FILTERED_EXCEPTION_PROPERTY` guarantees for the hand-reported path.
 */
export const shouldIgnoreClientError = (err: unknown): boolean => {
	const shape = toErrorShape(err);

	if (isAbortError(shape)) return true;
	if (isResizeObserverLoop(shape)) return true;
	if (isBrowserExtensionError(shape)) return true;
	if (isAnalyticsNetworkError(shape)) return true;
	if (isOfflineNetworkError(shape)) return true;

	if (isChunkLoadError(shape)) {
		// Already handled one this session: the reload either did not fix it (a
		// genuinely broken deploy) or has not happened yet. Either way the first
		// report already told us, and reporting the rest would let one tab
		// produce unbounded events.
		if (hasChunkLoadRetryHappened()) return true;
		// The first one: report it (return false) AND get onto the new build.
		if (markChunkLoadRetry()) scheduleChunkLoadReload();
		return false;
	}

	return false;
};

/** Flatten a posthog-js parsed stacktrace back into matchable source locations. */
const framesToStack = (stacktrace: unknown): string => {
	if (!stacktrace || typeof stacktrace !== 'object') return '';
	const frames = (stacktrace as { frames?: unknown }).frames;
	if (!Array.isArray(frames)) return '';
	return frames
		.map((frame) => {
			const filename = (frame as { filename?: unknown } | null)?.filename;
			return typeof filename === 'string' ? filename : '';
		})
		.filter(Boolean)
		.join('\n');
};

/**
 * `before_send` hook for posthog-js: drops autocaptured `$exception` events
 * that `shouldIgnoreClientError` rejects, and passes everything else through
 * untouched (this hook runs for EVERY event, not just exceptions).
 *
 * Exceptions `reportClientError` already filtered arrive tagged and skip the
 * predicate entirely - judging them a second time would re-run the one-shot
 * rules and reject an event that was just admitted. The tag is deleted here so
 * it never leaves the browser.
 */
export const dropIgnoredExceptionEvents = (
	result: CaptureResult | null,
): CaptureResult | null => {
	if (!result || result.event !== '$exception') return result;

	if (result.properties?.[PRE_FILTERED_EXCEPTION_PROPERTY]) {
		delete result.properties[PRE_FILTERED_EXCEPTION_PROPERTY];
		return result;
	}

	const list = result.properties?.$exception_list;
	const first = Array.isArray(list) ? list[0] : undefined;
	const shape: ErrorShape = {
		name: typeof first?.type === 'string' ? first.type : '',
		message: typeof first?.value === 'string' ? first.value : '',
		// posthog-js has already parsed the stack into frames by this point, so
		// rebuild just the source locations - that is all the extension check
		// looks at, and nothing here leaves the module.
		stack: framesToStack(first?.stacktrace),
	};

	return shouldIgnoreClientError(shape) ? null : result;
};

/** Test-only: clears the one-shot ChunkLoadError retry flag and reload latch. */
export const resetChunkLoadRetryState = (): void => {
	chunkLoadRetriedInMemory = false;
	chunkLoadReloadScheduled = false;
	if (typeof window === 'undefined') return;
	try {
		window.sessionStorage.removeItem(CHUNK_LOAD_RETRY_STORAGE_KEY);
	} catch {
		// Nothing to clear.
	}
};
